import React, {useState, useRef, useEffect} from 'react';
import styled from "styled-components";
import cookie from "react-cookies";
import {useRouter} from "next/router";
import {FilterContext} from "@providers/filters";
import {FilterDispatcherContext} from "@providers/filters";
import {saveCookie} from "@baseHelpers/cookie";
import Selection from "@molecules/selection/selection";
import {getSearchLink} from "@models/search";
import {getNorthCitiesIds} from "@models/area";
import {getCityById} from "@models/district";
import {citiesSelection} from "@hooks/cities";
import {getDistrictOptions} from "@hooks/districts";
import {getTypesOptions} from "@hooks/types";
import RadioListGroup from "@atoms/radio.list.group";
import ButtonBase from "@atoms/button.base";
import {isEmpty} from "@helpers/helpers";
import {UpdateSelectionOptions, pluckKeyToArray} from "@hooks/selection.options";
import {inArray, ObjectLength} from "@helpers/arrays";
import Icon from "@atoms/icon";
import SearchIcon from "@icons/search.svg";
import PinIcon from '@icons/pin.svg';


const CitiesLandingMainSection = ({defaultCityId, windowSize}) => {
    const districtsSelectionRef = useRef(null);
    const router = useRouter();
    const {selected_filters} = React.useContext(FilterContext);
    const filterDispatcher = React.useContext(FilterDispatcherContext);

    const [northCitiesIds, setNorthCitiesIds] = useState([]);
    const [cities, setCities] = useState({options: [], selected_items: ''});
    const [districts, setDistrict] = useState({options: [], selected_items: []});
    const [types, setTypes] = useState({options: [], selected_items: []});
    const [selectionLoading, setSelectionLoading] = useState({cities: false, districts: false, types: false});
    const [dealTypes, setDealTypes] = useState({
        options: [
            {value: 'sale', label: 'خرید', checked: true},
            {value: 'rent', label: 'رهن و اجاره', checked: false},
        ],
        selected_items: 'sale',
    });
    const [selectedCity, setSelectedCity] = useState(null);

    const selectedDistrictsItemsCopy = [...districts.selected_items];
    const selectedTypesItemsCopy = [...types.selected_items];
    let selectedFiltersDistrictIds = pluckKeyToArray(selected_filters.district_id, 'value');

    useEffect(() => {
        if (!isEmpty(defaultCityId)) {
            getCitiesItems(defaultCityId);
        }
    }, [defaultCityId]);

    useEffect(() => {
        if (!northCitiesIds.length) {
            getNorthCitiesIds().then(ids => {
                setNorthCitiesIds(ids);
            })
        }
    }, []);

    const getCitiesItems = async (city_id) => {
        const district_id = [];
        const types = [];

        if (!isEmpty(city_id)) {
            getCityById(city_id).then(city => {
                setSelectedCity(city);
            });
        }

        setSelectionLoading(prevState => {
            return {
                ...prevState,
                districts: true,
                types: true,
            }
        });

        const cities = await citiesSelection();
        const cities_items = await UpdateSelectionOptions(cities.options, {items: [city_id]});
        const districts = await getDistrictOptions(city_id);
        const TypesItems = await getTypesOptions(city_id);
        const new_districts = await UpdateSelectionOptions(districts.options, {items: district_id}, true);
        const new_types = await UpdateSelectionOptions(TypesItems.options, {items: types});

        setCities(cities_items);
        setDistrict(new_districts);
        setTypes(new_types);

        setSelectionLoading(prevState => {
            return {
                ...prevState,
                districts: false,
                types: false,
            }
        });

        await filterDispatcher({
            type: 'CITIES_OPTIONS',
            payload: {
                cities: cities_items,
                just_cities: true
            }
        });
    };

    const handleDealTypeChange = async (e) => {
        const value = e.target.value;
        const name = e.target.name;

        let newDealTypesOptions = [...dealTypes.options];

        for (let item of newDealTypesOptions) {
            item['checked'] = (item.value === value);
        }

        const newTypes = await getTypesOptions(cities.selected_items[0], value);
        const types_new_option = UpdateSelectionOptions(newTypes.options, {items: types.selected_items});

        setTypes(types_new_option);

        setDealTypes({
            options: newDealTypesOptions,
            selected_items: value,
        });
    };

    const handleCityChange = async (data) => {
        const city_id = data.items[0];

        if (!isEmpty(cities.selected_items) && !isEmpty(cities.selected_items[0]) && city_id === cities.selected_items[0]) {
            return false;
        }

        getCitiesItems(city_id);

        if (!isEmpty(districtsSelectionRef) && !isEmpty(districtsSelectionRef.current)) {
            districtsSelectionRef.current.firstChild.classList.remove('selected');
        }
    };

    const handleDistrictChange = async (data) => {

        if (JSON.stringify(data.items) !== JSON.stringify(selectedDistrictsItemsCopy)) {
            const new_option = await UpdateSelectionOptions(districts.options, data, true);

            cookie.remove('recent_districts');
            saveCookie('recent_districts', data.items.join(','));

            setDistrict(new_option);
        }
    };

    const handleTypeChange = (data) => {
        if (types.options.length) {
            const new_option = UpdateSelectionOptions(types.options, data);
            setTypes(new_option);
        }
    };

    const searchFilterBoxOnSubmitHandler = (e) => {
        e.preventDefault();

        const params = {
            city_id: cities.selected_items[0],
            deal_type: dealTypes.selected_items,
            district_id: pluckKeyToArray(districts.selected_items, 'value'),
            types: types.selected_items,
        };

        getSearchLink(params).then(result => {
            const location = document.location;
            const path = location.pathname + location.search;

            if (decodeURI(result.url) !== decodeURI(path)) {

                if (ObjectLength(result.params) > 0) {
                    router.push('/properties', result.url, {shallow: false, query: result.params})
                } else {
                    router.push('/properties', result.url);
                }
            }
        });
    };

    let propertyTypeSelectionType = (!isEmpty(selectedCity) && (inArray(selectedCity.id, northCitiesIds) || inArray(selectedCity.id, [207, 259]))) ? 'single' : 'multiple';

    return (
        <Container className="position-relative d-block d-lg-flex align-items-center">
            <Background className="bg-gray100"/>

            <div className="container mt-0 mt-lg-40 sm-fluid">
                <Card className="">
                    <div className="search-filter-card rounded-16 p-24 p-lg-48">
                        <h1 className="h1-bold">راهی سریع و مطمئن<br/>برای خانه‌دار شدن</h1>

                        <div className="d-flex align-items-center mt-24 mt-lg-40 search-filter-deal-type">
                            {
                                dealTypes.options.map((item, key) =>
                                    <RadioListGroup
                                        name="search_filter_deal_type"
                                        key={key}
                                        item={item}
                                        onChange={handleDealTypeChange}/>
                                )
                            }
                        </div>

                        <div className="modal-filter-body">
                            <div className="mt-16 mt-lg-24 cities-city-selection">
                                <Selection
                                    type="single"
                                    name="city"
                                    searchable
                                    loading={selectionLoading.cities}
                                    options={cities.options}
                                    label="شهر"
                                    icon={<PinIcon/>}
                                    onChange={(data) => handleCityChange(data)}
                                    clearabel={false}
                                    scroll_lock={(windowSize < 769)}
                                />
                            </div>

                            {
                                (!isEmpty(selectedCity) && selectedCity.type === 'city') ?
                                    <div ref={districtsSelectionRef} className="mt-16 mt-lg-24">
                                        <Selection
                                            type="multiple"
                                            name="district"
                                            searchable
                                            loading={selectionLoading.districts}
                                            options={districts.options}
                                            label="خیابان اصلی"
                                            onChange={(data) => handleDistrictChange(data)}
                                            limit_select={10}
                                            selected_items={selectedFiltersDistrictIds}
                                            appendSelectedItemsInTop={true}
                                            clearabel={true}
                                            scroll_lock={(windowSize < 769)}
                                        />
                                    </div>
                                    :
                                    ''
                            }

                            <div className="mt-16 mt-lg-24">
                                <Selection
                                    type={propertyTypeSelectionType}
                                    name="type"
                                    options={types.options}
                                    selected_items={selectedTypesItemsCopy}
                                    label="نوع ملک"
                                    onChange={(data) => handleTypeChange(data)}
                                    loading={(selectionLoading.types)}
                                    scroll_lock={(windowSize < 769)}
                                />
                            </div>
                        </div>

                        <ButtonBase className="mt-24 mt-lg-32" color="secondary" block onClick={(e) => searchFilterBoxOnSubmitHandler(e)}>
                            <Icon className="ml-8 cities-search-icon" width={15}>
                                <SearchIcon/>
                            </Icon>
                            جستجو
                        </ButtonBase>
                    </div>
                </Card>
            </div>
        </Container>
    );
};

function propsAreEqual(prevProps, nextProps) {
    return (prevProps.windowSize === nextProps.windowSize && prevProps.defaultCityId === nextProps.defaultCityId);
}

export default React.memo(CitiesLandingMainSection, propsAreEqual);

// styles

const Container = styled.div`
    min-height: 700px;
    height: 100vh;
    
    @media (max-width: ${props => props.theme.screenSmMax}) {
        min-height: auto;
        height: auto;
        
        .selection-dropdown-wrapper {
            border-radius: 0;
        }
    }
`;

const Background = styled.div`
    background-image: url("/_img/landings/welcome/main-d.jpg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: absolute;
    width: 100%;
    height: 100%;
    
    @media (max-width: ${props => props.theme.screenSmMax}) {
        position: relative;
        background-image: url("/_img/landings/welcome/main-m.jpg");
        height: 350px;
    }
`;

const Card = styled.div`
    width: 400px;
    
    h1 {
        line-height: 1.67;
    }
    
    @media (max-width: ${props => props.theme.screenSmMax}) {
        width: 100%;
        padding: 40px 24px 0;
        position: relative;
        top: -24px;
        border-radius: 24px 24px 0 0;
        background-color: #ffffff;
        
        .main-content-button {
            width: 100%;
        }
    }
    
    .landing-search-box {
        width: 400px;

        @media (max-width: ${props => props.theme.screenSmMax}) {
            width: 100%;
        }
    }

    .search-filter-deal-type {
        label {
            border: none;
            padding: 4px 16px;
            border-radius: 25px;
        }
        input {
            visibility: hidden;
            display: none;
            
            &:checked + :last-child{
                  color: ${props => props.theme.secondary};
                  background-color: #e7f4f0;
                  font-weight: bold;
            }
        }
    }

    .search-filter-card {
        position: relative;
        background-color: #fff;
        box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.16);

        @media (max-width: ${props => props.theme.screenSmMax}) {
            box-shadow: none;
            padding: 0 !important;
        }
    }

    .cities-city-selection {
        .selection {
            border-color: #222;
        }
    }

    .selection {
        border: solid 1px #ccd0d2;
        border-radius: 8px;
        height: 40px;
    
        &.selected {
            border-color: #222;
        }
    
        .selection-toggle {
            padding: 8px 16px;
    
            &:after {
                left: 8px;
            }
    
            span {
                margin-left: -4px !important;
            }
        }
    
        button {
            margin-left: 8px;
        }
    }
    
    .cities-search-icon {
        svg {
            g {
                stroke-width: 2;
            }
        }
    }
`;
